import React, { TextareaHTMLAttributes, forwardRef, useId } from 'react';
import { cn } from '@/lib/utils';

type TextareaVariant = 'default' | 'filled' | 'minimal';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    error?: string;
    label?: string;
    helperText?: string;
    containerClassName?: string;
    variant?: TextareaVariant;
    minRows?: number;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
    ({
        className = '',
        error,
        label,
        helperText,
        containerClassName = '',
        variant = 'default',
        minRows = 3,
        id: idProp,
        name: nameProp,
        ...props
    }, ref) => {
        const generatedId = useId();
        const textareaId = idProp ?? generatedId;
        const textareaName = nameProp ?? textareaId;

        if (variant === 'minimal') {
            return (
                <textarea
                    ref={ref}
                    id={textareaId}
                    name={textareaName}
                    rows={minRows}
                    className={cn(
                        'w-full resize-none border-0 bg-transparent p-0 text-sm text-default outline-none focus:ring-0',
                        className,
                    )}
                    {...props}
                />
            );
        }

        return (
            <div className={cn('input-wrap', containerClassName)}>
                {label && (
                    <label htmlFor={textareaId} className="input-label">
                        {label}
                    </label>
                )}
                <textarea
                    ref={ref}
                    id={textareaId}
                    name={textareaName}
                    rows={minRows}
                    className={cn(
                        'input',
                        variant === 'filled' && 'input-filled',
                        error && 'input-error-state',
                        className,
                    )}
                    {...props}
                />
                {error && <p className="input-error">{error}</p>}
                {helperText && !error && <p className="input-hint">{helperText}</p>}
            </div>
        );
    }
);

Textarea.displayName = 'Textarea';

export default Textarea;
